import * as _ from 'lodash';

import { PreorderArticle, PreorderArticleSize, PrepackForPreorder } from 'app/b2b-order/types/preorder';
import { GtinMap } from 'types';
import { OrderProduct, HistoricalOrderArticle } from 'app/order-management/services/order-history/searcher.types';
import { InteractiveCell } from 'app/b2b-order/components/order/types';
import { Router } from '@angular/router';
import { Branch } from 'models';
import { OrderArticleColor } from 'app/b2b-order/types/order';
import { ReorderArticleSize, ReorderPrepack, DefaultSelectionType, ReorderArticle } from 'app/b2b-order/types/reorder';

export function getTotalPriceByItem(article: PreorderArticle | ReorderArticle): number {
    const normalizedArticle = flattenMultiStoreArticle(article);

    const totalPricePerSize = _.map(
        normalizedArticle.sizes,
        ({ stock, price: sizePrice }: PreorderArticleSize | ReorderArticleSize<number>) => {
            const price = sizePrice
                ? sizePrice.discountedPurchasePrice || sizePrice.purchasePrice
                : normalizedArticle.price.discountedPurchasePrice || normalizedArticle.price.purchasePrice;
            return price * stock.selectedAmount;
        }
    );

    const totalPricePerPrepack = _.map(normalizedArticle.prepacks, (prepack) =>
        getPriceForPrepack(normalizedArticle, prepack as PrepackForPreorder | ReorderPrepack<number>)
    );

    return _.sum(totalPricePerSize) + _.sum(totalPricePerPrepack);
}

export const getPriceForPrepack = (
    article: PreorderArticle | ReorderArticle,
    prepack: PrepackForPreorder | ReorderPrepack<number>
) =>
    prepack.stock.selectedAmount *
    _.sum(
        _.map(prepack.products, ({ quantity, price: sizePrice }) => {
            const price = sizePrice
                ? sizePrice.discountedPurchasePrice || sizePrice.purchasePrice
                : article.price.discountedPurchasePrice || article.price.purchasePrice;
            return price * quantity;
        })
    );

export function calculateTotalAmountMapPerArticle(article: PreorderArticle | ReorderArticle): AmountPerGtin {
    const singleProductsTotalAmount = getAmountPerGtinFromProducts(article.sizes as ReorderArticleSize[]);
    const prepacksTotalAmount = _.map(article.prepacks, getAmountPerGtinFromPrepacks);

    const mergedPrepacksProductsTotalAmount = _.mergeWith(
        {},
        ...prepacksTotalAmount,
        (totalGtinAmount: any, gtinAmount: any, gtin: string | number, gtinAmountMap: { [x: string]: any }) =>
            gtinAmountMap[gtin] ? totalGtinAmount + gtinAmount : gtinAmount
    );

    return getTotalAmountPerGtinMap(singleProductsTotalAmount, mergedPrepacksProductsTotalAmount);
}

export function containsInsufficientQuantityError(errorData: ErrorData) {
    return errorData.type === 'OrderingProcessError' && _.some(errorData.errors, { type: 'InsufficientQuantity' });
}

export function containsInvalidOrderQuantityIncrementError(errorData: ErrorData) {
    return (
        errorData.type === 'OrderingProcessError' && _.some(errorData.errors, { type: 'InvalidOrderQuantityIncrement' })
    );
}

export function containsStoreWithoutAccessError(errorData: ErrorData) {
    return errorData.type === 'OrderingProcessError' && _.some(errorData.errors, { type: 'StoreWithoutAccess' });
}

function getAmountPerGtinFromProducts(sizes: ReorderArticleSize[]): AmountPerGtin {
    const selectedAmountMappedBySizeGtin = _.reduce(
        sizes,
        (gtinMap, size) => ({
            ...gtinMap,
            [size.gtin]: getMatrixSelectedAmount(size.stock.selectedAmount)
        }),
        {}
    );
    return selectedAmountMappedBySizeGtin;
}

function getAmountPerGtinFromPrepacks(prepack: PrepackForPreorder): AmountPerGtin {
    const products = prepack.products;
    const mapPerGtinForPrepack = _.reduce(
        products,
        (gtinMap, product) => ({
            ...gtinMap,
            [product.gtin]: prepack.stock.selectedAmount * product.quantity
        }),
        {}
    );
    return mapPerGtinForPrepack;
}

function getTotalAmountPerGtinMap(
    singleProductsAmount: AmountPerGtin,
    prepacksProductsWithAmount: AmountPerGtin
): AmountPerGtin {
    const totalMap = { ...prepacksProductsWithAmount, ...singleProductsAmount };

    Object.keys(singleProductsAmount).forEach((gtin) => {
        if (prepacksProductsWithAmount[gtin]) {
            totalMap[gtin] = prepacksProductsWithAmount[gtin] + singleProductsAmount[gtin];
        }
    });

    return totalMap;
}

export function selectedAmountIncludesBackorderStock(
    selectedAmount: number,
    stock: { available: number; total: number }
) {
    return selectedAmount > stock.available && selectedAmount <= stock.total;
}

export function itemHasStock(item: ReorderPrepack | ReorderArticleSize) {
    return item.stock.total > 0;
}

export function itemHasBackorder(item: ReorderPrepack | ReorderArticleSize) {
    return !!item.stock.backorder;
}

export function is2DMatrix({
    sizes,
    prepacks
}: {
    sizes: (ReorderArticleSize | PreorderArticleSize)[];
    prepacks: (ReorderPrepack | PrepackForPreorder)[];
}) {
    return (
        _.every(sizes, 'detailedSize.size') &&
        _.every(sizes, 'detailedSize.subSize') &&
        (prepacks ? _.isEmpty(prepacks) : true)
    );
}

export function getCompoundSizeProductIdentifier(article: ReorderArticleSize | PreorderArticleSize) {
    return `${article.detailedSize.size}/${article.detailedSize.subSize}`;
}

export function getHistoricalOrderPrice(items) {
    const totalPrice = items.reduce((total, item) => {
        const subTotal = getHistoricalTotalPricePerItem(item.formattedArticles);

        if (!subTotal || _.isNil(total)) {
            return null;
        }

        return total + subTotal;
    }, 0);

    return totalPrice;
}

function getHistoricalTotalPricePerItem(articles) {
    return articles.reduce((total, article) => {
        if (!article.totalPrice.value || _.isNil(total)) {
            return null;
        }
        return total + article.totalPrice.value;
    }, 0);
}

export function getOrderArticlePrice(sizes: ReorderArticleSize[]) {
    const price = sizes.reduce((price, size) => {
        if (price === null || !size.price.purchasePrice) {
            return null;
        }

        return !size.price.purchasePrice
            ? null
            : price + size.price.purchasePrice * getMatrixSelectedAmount(size.stock.selectedAmount);
    }, 0);

    return price;
}

export function joinSizesWithOrderPrices(
    article: HistoricalOrderArticle,
    orderArticles: OrderProduct[]
): (ReorderArticleSize & { price: { purchasePrice: number; currency: string } })[] {
    return article.sizes.map((size) => {
        const { price } = _.find(orderArticles, (orderArticle) => orderArticle.gtin === size.gtin);
        return {
            ...size,
            price: {
                purchasePrice: price?.value,
                currency: price?.currency
            }
        };
    });
}

export function getStoreSelectedAmount(selectedAmount: DefaultSelectionType, storeId: string): number {
    return typeof selectedAmount === 'number' ? selectedAmount : selectedAmount[storeId];
}

export function getMatrixSelectedAmount(selectedQuantity: InteractiveCell['selectedQuantity']): number {
    if (typeof selectedQuantity === 'number') {
        return selectedQuantity;
    }

    return selectedQuantity ? _.sum(Object.values(selectedQuantity)) : null;
}

export function flattenMultiStoreArticle(article: PreorderArticle | ReorderArticle, storeId?: string) {
    const normalizedArticle = _.cloneDeep(article);
    const fetchAmount = storeId
        ? (selectedAmount: DefaultSelectionType) => getStoreSelectedAmount(selectedAmount, storeId)
        : getMatrixSelectedAmount;

    normalizedArticle.sizes.forEach((size) => {
        size.stock.selectedAmount = fetchAmount(size.stock.selectedAmount);
    });

    normalizedArticle.prepacks.forEach((prepack) => {
        prepack.stock.selectedAmount = fetchAmount(prepack.stock.selectedAmount);
    });

    return normalizedArticle;
}

export function isNumberedSize(size: string) {
    return !Number.isNaN(Number(size));
}

export function isRoute(router: Router, route: string) {
    return router.isActive(route, {
        paths: 'subset',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored'
    });
}

export function getStoreLabel(store: Branch) {
    return store.shortName || store.name || store.address.address1;
}

export function getColorDropdownLabel(color: OrderArticleColor) {
    const { colorGroupId, name } = color;

    if (!name) {
        return colorGroupId;
    }

    const words = [...colorGroupId.split(' '), ...name.split(' ')];

    // Remove duplicates while preserving case sensitivity
    const wordMap: { [key: string]: string } = {};
    words.forEach((word) => {
        wordMap[word.toLowerCase()] = word;
    });

    const uniqueWords = Object.values(wordMap);
    const [firstWord, ...remainingWords] = uniqueWords;

    const isNumber = (input: string) => Number.isInteger(Number(input));

    if (isNumber(firstWord)) {
        return `${firstWord}-${remainingWords.join(' ')}`;
    }

    return uniqueWords.join(' ');
}

export type AmountPerGtin = GtinMap<number>;

export interface ErrorData {
    errors: any[];
    type: string;
    message: string;
}

export enum ReorderPagesWithSidebar {
    Shop = 'shop',
    AutoRestock = 'autoRestock',
    Suggestions = 'suggestions'
}

export enum ReorderPagesWithoutSidebar {
    Insights = 'insights',
    AddArticle = 'addArticle',
    StrategyCreation = 'strategyCreation'
}

export enum ReorderPagesWithoutShoppingCart {
    AutoRestock = 'autoRestock',
    Suggestions = 'suggestions',
    Insights = 'insights',
    AddArticle = 'addArticle',
    StrategyCreation = 'strategyCreation'
}

export enum ReorderPagesWithStores {
    Shop = 'shop',
    BrandOverview = 'brand-overview',
    ShoppingCart = 'shopping-cart',
    Product = 'product'
}
